import { createApp, type App as VueApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { createPinia } from "pinia";
import { ElMessage } from "element-plus";

// Element Plus 命令式组件（ElMessageBox/ElMessage/ElNotification/ElLoading）的基础样式。
// 这些 API 通过显式 import 调用，不会触发 unplugin 的按需样式注入，必须在入口手动引入，
// 否则弹框缺少内部布局（无边框感、按钮不右对齐、遮罩不居中）。
// 放在设计系统/主题之前，确保 App.vue 中的定制覆盖（!important / 更高特异性）优先生效。
import "element-plus/theme-chalk/el-overlay.css";
import "element-plus/theme-chalk/el-message-box.css";
import "element-plus/theme-chalk/el-message.css";
import "element-plus/theme-chalk/el-notification.css";
import "element-plus/theme-chalk/el-loading.css";

// 导入蒲公英设计系统
import "@/assets/styles/design-system.css";
// 导入主题样式
import "@/assets/styles/themes/index.css";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";

import {
  lazyLoadDirective,
  registerClickOutside,
  registerSafeHtml,
} from "@/directives";
import { cacheManager } from "@/utils/cache";
import { createModuleLogger } from "@shared/utils/Logger";
const logger = createModuleLogger("main");

const app: VueApp = createApp(App);
const pinia = createPinia();

// 注册性能优化指令（从 shared 共享内核导入）
app.use(lazyLoadDirective);
registerClickOutside(app);
// 等保三级：注册安全 HTML 渲染指令，用于替代 v-html 渲染 AI 输出
registerSafeHtml(app);

// 初始化缓存管理器（定期清理过期缓存）
if (import.meta.env.PROD) {
  // 生产环境每小时清理一次缓存
  setInterval(
    () => {
      cacheManager.cleanup();
    },
    60 * 60 * 1000,
  );
}

// 全局错误处理 - 屏蔽浏览器扩展相关错误
app.config.errorHandler = (err: unknown, _vm: unknown, info: string) => {
  // 过滤掉浏览器扩展相关的错误
  const extensionErrors = [
    "background-clear-trans-state",
    "chrome-extension",
    "moz-extension",
    "safari-extension",
    "edge-extension",
    "The message port closed before a response was received",
    "Extension context invalidated",
    "lodash-CTh9qzyE.js",
    "Script error",
    "Non-Error promise rejection captured",
  ];

  const errorMessage =
    (err as Error)?.message || (err as Error)?.toString() || "";
  const errorStack = (err as Error)?.stack || "";
  const errorString = JSON.stringify(err) || "";

  const isExtensionError = extensionErrors.some(
    (keyword) =>
      errorMessage.includes(keyword) ||
      errorStack.includes(keyword) ||
      errorString.includes(keyword),
  );

  if (!isExtensionError) {
    // 只记录应用相关的错误
    console.error("[Vue应用错误]:", err, info);
    // 可以在这里添加错误上报逻辑
  }
};

// 全局未捕获的Promise rejection处理
let lastRejectionErrorTime = 0;
const REJECTION_ERROR_THROTTLE = 5000; // 5秒内只记录一次网络错误

window.addEventListener(
  "unhandledrejection",
  (event: PromiseRejectionEvent) => {
    const error = event.reason;
    const extensionErrors = [
      "background-clear-trans-state",
      "chrome-extension",
      "moz-extension",
      "safari-extension",
      "edge-extension",
      "The message port closed before a response was received",
      "Extension context invalidated",
      "lodash-CTh9qzyE.js",
      "Script error",
      "Non-Error promise rejection captured",
    ];

    // 网络错误关键词
    const networkErrorKeywords = [
      "ERR_CONNECTION_REFUSED",
      "Network Error",
      "ERR_NETWORK",
      "Failed to fetch",
    ];

    const errorMessage = error?.message || error?.toString() || "";
    const errorStack = error?.stack || "";
    const errorString = JSON.stringify(error) || "";

    const isExtensionError = extensionErrors.some(
      (keyword) =>
        errorMessage.includes(keyword) ||
        errorStack.includes(keyword) ||
        errorString.includes(keyword),
    );

    const isNetworkError = networkErrorKeywords.some(
      (keyword) =>
        errorMessage.includes(keyword) ||
        errorStack.includes(keyword) ||
        errorString.includes(keyword) ||
        error?.code === "ERR_NETWORK" ||
        error?.code === "ERR_CONNECTION_REFUSED",
    );

    if (isExtensionError) {
      // 阻止扩展错误显示在控制台
      event.preventDefault();
      return;
    }

    if (isNetworkError) {
      // 节流网络错误日志
      const now = Date.now();
      if (now - lastRejectionErrorTime > REJECTION_ERROR_THROTTLE) {
        lastRejectionErrorTime = now;
        console.warn(
          "[网络连接错误]:",
          "后端服务连接失败，请检查后端服务是否正常运行",
        );
      }
      event.preventDefault();
      return;
    }

    // 应用相关的错误正常处理
    console.error("[未捕获的Promise错误]:", error);
  },
);

// 额外的错误过滤 - 专门处理lodash相关的扩展错误和网络连接错误
const originalConsoleError = console.error;
let lastNetworkErrorTime = 0;
const NETWORK_ERROR_THROTTLE = 5000; // 5秒内只记录一次网络错误

console.error = function (...args: unknown[]) {
  const message = args.join(" ");

  // 过滤浏览器扩展错误
  const extensionKeywords = [
    "background-clear-trans-state",
    "lodash-CTh9qzyE.js",
    "Uncaught (in promise) {cmd: 'background-clear-trans-state'",
    "chrome-extension",
    "moz-extension",
    "A listener indicated an asynchronous response by returning true",
    "message channel closed before a response was received",
  ];

  const isExtensionError = extensionKeywords.some((keyword) =>
    message.includes(keyword),
  );

  // 过滤网络连接错误（节流处理，避免重复日志）
  const networkErrorKeywords = [
    "ERR_CONNECTION_REFUSED",
    "Network Error",
    "Failed to load resource",
    "net::ERR_CONNECTION_REFUSED",
    "ERR_NETWORK",
  ];

  const isNetworkError = networkErrorKeywords.some((keyword) =>
    message.includes(keyword),
  );

  if (isExtensionError) {
    // 完全过滤扩展错误
    return;
  }

  if (isNetworkError) {
    // 节流网络错误日志
    const now = Date.now();
    if (now - lastNetworkErrorTime > NETWORK_ERROR_THROTTLE) {
      lastNetworkErrorTime = now;
      originalConsoleError.apply(console, args);
    }
    return;
  }

  // 其他错误正常输出
  originalConsoleError.apply(console, args);
};

app.use(pinia);
app.use(router);
app.config.globalProperties.$message = ElMessage;

// 注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

// PWA：注册 Service Worker
// 让蒲公英可"安装"为桌面应用，获得无地址栏、置顶的应用窗口
// 仅在 VITE_ENABLE_PWA=true 且生产环境时注册（与 vite.config.ts 中 VitePWA 插件开关注释同步）
if (
  "serviceWorker" in navigator &&
  import.meta.env.PROD &&
  import.meta.env.VITE_ENABLE_PWA === "true"
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", {
        type: "module",
      })
      .then((registration) => {
        logger.info("[PWA] Service Worker 注册成功:", registration.scope);
      })
      .catch((error) => {
        console.warn("[PWA] Service Worker 注册失败:", error);
      });
  });
}

// 开发态组件选择器（仅 DEV 且 URL 带 ?inspect=1）：点选元素复制组件名+文件路径
if (import.meta.env.DEV && new URLSearchParams(window.location.search).has("inspect")) {
  void import("./dev/componentInspector").then(({ installComponentInspector }) => {
    installComponentInspector();
  });
}

// 先挂载应用，认证恢复由路由守卫负责。
// 避免 refresh_token 接口慢或不可用时，根路径首屏卡在 app.mount() 之前形成白屏。
app.mount("#app");
